import { SelectHTMLAttributes, forwardRef } from 'react'
import { ChevronDown } from 'lucide-react'

interface DropdownOption {
  value: string
  label: string
}

interface DropdownProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: string
  error?: string
  helperText?: string
  options: DropdownOption[]
  placeholder?: string
}

export const Dropdown = forwardRef<HTMLSelectElement, DropdownProps>(
  (
    {
      label,
      error,
      helperText,
      options,
      placeholder = 'เลือก...',
      className = '',
      ...props
    },
    ref
  ) => {
    const selectClasses = error
      ? 'border-error focus:ring-error'
      : 'border-border-medium focus:ring-primary-800 focus:border-primary-800'

    return (
      <div className="w-full">
        {label && (
          <label className="block text-sm font-medium text-text-primary mb-2">
            {label}
          </label>
        )}

        <div className="relative">
          <select
            ref={ref}
            className={`
              w-full h-10
              px-4 py-2 pr-10
              border rounded-lg
              text-base text-text-primary
              bg-white
              appearance-none
              focus:outline-none focus:ring-2
              disabled:bg-background-tertiary disabled:text-text-disabled disabled:cursor-not-allowed
              transition-all duration-200
              ${selectClasses}
              ${className}
            `}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <ChevronDown className="w-5 h-5 text-text-tertiary" />
          </div>
        </div>

        {error && (
          <p className="mt-1 text-sm text-error">{error}</p>
        )}

        {helperText && !error && (
          <p className="mt-1 text-sm text-text-tertiary">{helperText}</p>
        )}
      </div>
    )
  }
)

Dropdown.displayName = 'Dropdown'

export default Dropdown
